import { useCallback, useEffect, useState } from 'react'

export const useRelatedDataNavigation = ({
  selectedDevices,
  setSelectedDevices,
  setSelectedMeasurements,
  dispatchChartOptions,
  availableMeasurements,
  chartAreaRef,
}) => {
  const [deviceJustSet, setDeviceJustSet] = useState(null)
  const [measurementToSet, setMeasurementToSet] = useState(null)
  const [isExternallySetCustom, setIsExternallySetCustom] = useState(false)

  const handleViewRelatedData = useCallback(
    ({ device, measurement, time, minutesBefore, minutesAfter }) => {
      const eventTime = new Date(time).getTime()

      const defaultMinutesBefore = device && measurement ? 2 : 5
      const defaultMinutesAfter = device && measurement ? 2 : 60

      const actualMinutesBefore = minutesBefore ?? defaultMinutesBefore
      const actualMinutesAfter = minutesAfter ?? defaultMinutesAfter

      const startTimeMs = eventTime - (actualMinutesBefore * 60 * 1000)
      const endTimeMs = eventTime + (actualMinutesAfter * 60 * 1000)

      if (device && measurement) { // Typically from FaultsTable
        const devicesWereEmpty = selectedDevices.length === 0

        setSelectedDevices(prevDevices =>
          prevDevices.includes(device) ? prevDevices : [...prevDevices, device],
        )

        if (devicesWereEmpty) {
          setDeviceJustSet(device)
          setMeasurementToSet(measurement)
          setSelectedMeasurements([]) // Clear previous measurements, effect will set new one
        } else {
          setSelectedMeasurements(prevMeasurements =>
            prevMeasurements.includes(measurement) ? prevMeasurements : [...prevMeasurements, measurement],
          )
          setDeviceJustSet(null)
          setMeasurementToSet(null)
        }
      } else { // Typically from ConfigChangesTable (device/measurement are null)
        setDeviceJustSet(null)
        setMeasurementToSet(null)
      }

      const newTimeRange = {
        type: 'absolute',
        startTime: new Date(startTimeMs),
        endTime: new Date(endTimeMs),
        duration: 'custom',
      }
      dispatchChartOptions({ type: 'SET_TIME_RANGE', payload: newTimeRange })
      setIsExternallySetCustom(true)

      setTimeout(() => {
        if (chartAreaRef.current) {
          chartAreaRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      }, 300)
    },
    [selectedDevices, setSelectedDevices, setSelectedMeasurements, dispatchChartOptions, chartAreaRef],
  )

  useEffect(() => {
    if (deviceJustSet && measurementToSet && selectedDevices.includes(deviceJustSet)) {
      if (availableMeasurements.includes(measurementToSet)) {
        setSelectedMeasurements([measurementToSet])
      }
      // Always clear the trigger states after processing this specific navigation intent.
      setDeviceJustSet(null)
      setMeasurementToSet(null)
    }
  }, [deviceJustSet, measurementToSet, selectedDevices, availableMeasurements, setSelectedMeasurements])

  const handleExternalCustomProcessed = useCallback(() => {
    setIsExternallySetCustom(false)
  }, [])

  return {
    handleViewRelatedData,
    isExternallySetCustom,
    handleExternalCustomProcessed,
  }
}
